import React from 'react';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const CharacterSkeleton = () => {
  return (
    <ContentWrapper>
      <Card>
        <AvatarSection>
          <SkeletonAvatar />
        </AvatarSection>
        <ContentSection>
          <SkeletonTitle />
          <DescriptionBlock>
            <SkeletonDescription />
            <SkeletonDescription width="95%" />
            <SkeletonDescription width="90%" />
          </DescriptionBlock>
          <AboutSection>
            <AboutTitle>About</AboutTitle>
            <TagWrapper>
              {[...Array(8)].map((_, index) => (
                <SkeletonTag
                  key={index}
                  width={`${Math.floor(Math.random() * (160 - 100) + 100)}px`}
                />
              ))}
            </TagWrapper>
          </AboutSection>
        </ContentSection>
      </Card>

      <GallerySection>
        <SkeletonSectionTitle />
        <GalleryGrid>
          {[...Array(6)].map((_, index) => (
            <SkeletonGalleryImage key={index} />
          ))}
        </GalleryGrid>
      </GallerySection>

      <ScenariosSection>
        <SkeletonSectionTitle width="280px" />
        <ScenariosGrid>
          {[...Array(4)].map((_, index) => (
            <ScenarioCard key={index}>
              <SkeletonScenarioImage />
              <ScenarioContent>
                <SkeletonScenarioTitle />
                <SkeletonScenarioText />
                <SkeletonScenarioText width="90%" />
                <SkeletonScenarioText width="85%" />
                <SkeletonPlayButton />
              </ScenarioContent>
            </ScenarioCard>
          ))}
        </ScenariosGrid>
      </ScenariosSection>
    </ContentWrapper>
  );
};

export default CharacterSkeleton;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 56px;
  position: relative;
  margin-top: 24px;

  @media (max-width: 720px) {
    gap: 24px;
    margin-top: 0;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: row;
  border-radius: 16px;
  overflow: hidden;
  width: 100%;
  margin: 0 auto;
  color: #fff;
  background: #181818;
  min-height: 336px;
  position: relative;

  @media (max-width: 600px) {
    flex-direction: column;
    border-radius: 12px;
    min-height: unset;
    max-width: 343px;
    margin: 0 auto 24px auto;
    background: #181818;
    padding-bottom: 24px;
    box-shadow: 0 2px 12px 0 rgba(0,0,0,0.10);
  }
`;

const AvatarSection = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #232323;
  width: 261px;
  height: 336px;

  @media (max-width: 600px) {
    width: 100%;
    height: 441px;
    background: none;
    padding: 0;
    aspect-ratio: 7/9;
    border-radius: 12px 12px 0 0;
    overflow: hidden;
  }
`;

const SkeletonAvatar = styled.div`
  width: 261px;
  height: 336px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #1D1D1D;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;

  @media (max-width: 600px) {
    width: 100%;
    height: 100%;
    aspect-ratio: 7/9;
    border-radius: 12px 12px 0 0;
    max-width: 343px;
    max-height: 441px;
    min-height: 220px;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 32px 32px 32px 40px;
  flex: 1;
  gap: 18px;
  max-height: 336px;
  width: 100%;
  overflow-y: auto;

  @media (max-width: 600px) {
    padding: 20px 20px 0 20px;
    align-items: flex-start;
    gap: 18px;
    height: auto;
    overflow-y: visible;
    max-height: none;
  }
`;

const SkeletonTitle = styled.div`
  width: 60%;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #1D1D1D;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
`;

const DescriptionBlock = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkeletonDescription = styled.div<{ width?: string }>`
  width: ${props => props.width || '100%'};
  height: 18px;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #1D1D1D;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
`;

const AboutSection = styled.div`
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const AboutTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  color: #696969;
  margin-bottom: 10px;
`;

const TagWrapper = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 18px;

  @media (max-width: 600px) {
    justify-content: flex-start;
    gap: 10px;
  }
`;

const SkeletonTag = styled.div<{ width?: string }>`
  width: ${props => props.width || 'auto'};
  height: 34px;
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #131313;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;

  @media (max-width: 600px) {
    height: 28px;
  }
`;

const GallerySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const SkeletonGalleryImage = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 12px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #1D1D1D;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
`;

const ScenariosSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ScenariosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ScenarioCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
  background: #1D1D1D;
`;

const SkeletonScenarioImage = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #1D1D1D;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
`;

const ScenarioContent = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SkeletonScenarioTitle = styled.div`
  width: 70%;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #1D1D1D;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
`;

const SkeletonScenarioText = styled.div<{ width?: string }>`
  width: ${props => props.width || '100%'};
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #1D1D1D;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
`;

const SkeletonPlayButton = styled.div`
  width: 86px;
  height: 50px;
  border-radius: 100px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #1D1D1D;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
  margin-left: auto;
  margin-top: 8px;
`;

const SkeletonSectionTitle = styled.div<{ width?: string }>`
  width: ${props => props.width || '160px'};
  height: 40px;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(169, 169, 169, 0.15) 0%, rgba(169, 169, 169, 0.45) 52.14%, rgba(169, 169, 169, 0.15) 100%), #1D1D1D;
  background-size: 2000px 100%;
  animation: ${shimmer} 2s linear infinite;
  margin-bottom: 2px;

  @media (max-width: 720px) {
    height: 20px;
    width: ${props => props.width || '100px'};
  }
`; 